import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Button, Dialog, TextField, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/system';
import {
  FontTextXlNormal,
  FontTextLgBold,
  FontTextBaseNormal,
  FontTextSmSemibold,
  FontTextSmBold,
  FontTextSmNormal,
  PrimitivesWhite,
  PrimitivesBlue900,
  PrimitivesBlue700,
  PrimitivesBluegray50,
  PrimitivesBluegray100,
  PrimitivesBluegray200,
  PrimitivesBluegray600,
  PrimitivesBluegray900,
  PrimitivesGray300,
  PrimitivesGray400,
  PrimitivesGreen500,
  PrimitivesRed50,
  PrimitivesRed500,
  PrimitivesRed700,
  PrimitivesRed800,
  SemanticTokensColorBgButtonNeutralSecondaryHover,
} from '@ambulnz/design-tokens';
import CloseIcon from '../../common/icons/close';
import ChangesSection from './ChangesSection';
import PayerCardsGrid from './PayerCardsGrid';
import { drawerWidth, toolbarHeight } from '../../../theme';
import AuditLogContainer from '../AuditLog/AuditLog.container';
import AmbTooltip from '../../common/AmbTooltip/AmbTooltip';

const MagicWandIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.1502 13.0501L14.0002 16.5001C13.8169 16.7834 13.5627 16.9001 13.2377 16.8501C12.9127 16.8001 12.7085 16.6084 12.6252 16.2751L11.9252 13.4751L5.1002 20.3001C4.91686 20.4834 4.6877 20.5792 4.4127 20.5876C4.1377 20.5959 3.9002 20.5001 3.7002 20.3001C3.51686 20.1167 3.4252 19.8834 3.4252 19.6001C3.4252 19.3167 3.51686 19.0834 3.7002 18.9001L10.5252 12.0501L7.7252 11.3501C7.39186 11.2667 7.2002 11.0626 7.1502 10.7376C7.1002 10.4126 7.21686 10.1584 7.5002 9.97505L10.9502 7.85005L10.6502 3.77505C10.6169 3.44172 10.7502 3.20005 11.0502 3.05005C11.3502 2.90005 11.6252 2.93339 11.8752 3.15005L15.0002 5.77505L18.7752 4.25005C19.0919 4.11672 19.3669 4.16672 19.6002 4.40005C19.8335 4.63339 19.8835 4.90839 19.7502 5.22505L18.2252 9.00005L20.8502 12.1001C21.0669 12.3501 21.1002 12.6251 20.9502 12.9251C20.8002 13.2251 20.5585 13.3584 20.2252 13.3251L16.1502 13.0501ZM3.3502 6.35005C3.2502 6.25005 3.2002 6.13339 3.2002 6.00005C3.2002 5.86672 3.2502 5.75005 3.3502 5.65005L4.6502 4.35005C4.7502 4.25005 4.86686 4.20005 5.0002 4.20005C5.13353 4.20005 5.2502 4.25005 5.3502 4.35005L6.6502 5.65005C6.7502 5.75005 6.8002 5.86672 6.8002 6.00005C6.8002 6.13339 6.7502 6.25005 6.6502 6.35005L5.3502 7.65005C5.2502 7.75005 5.13353 7.80005 5.0002 7.80005C4.86686 7.80005 4.7502 7.75005 4.6502 7.65005L3.3502 6.35005ZM13.8752 12.9251L15.0752 10.9501L17.4002 11.1251L15.9002 9.35005L16.7752 7.20005L14.6252 8.07505L12.8502 6.60005L13.0252 8.90005L11.0502 10.1251L13.3002 10.6751L13.8752 12.9251ZM17.6502 20.6501L16.3502 19.3501C16.2502 19.2501 16.2002 19.1334 16.2002 19.0001C16.2002 18.8667 16.2502 18.7501 16.3502 18.6501L17.6502 17.3501C17.7502 17.2501 17.8669 17.2001 18.0002 17.2001C18.1335 17.2001 18.2502 17.2501 18.3502 17.3501L19.6502 18.6501C19.7502 18.7501 19.8002 18.8667 19.8002 19.0001C19.8002 19.1334 19.7502 19.2501 19.6502 19.3501L18.3502 20.6501C18.2502 20.7501 18.1335 20.8001 18.0002 20.8001C17.8669 20.8001 17.7502 20.7501 17.6502 20.6501Z"
      fill={PrimitivesBluegray900}
    />
  </svg>
);

const GreenCheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path
      d="M8.9375 13L13.8958 8.0625L12.8333 7L8.9375 10.875L7.16667 9.125L6.10417 10.1875L8.9375 13ZM10 18C8.90278 18 7.86806 17.7917 6.89583 17.375C5.92361 16.9583 5.07292 16.3854 4.34375 15.6563C3.61458 14.9271 3.04167 14.0764 2.625 13.1042C2.20833 12.1319 2 11.0972 2 10C2 8.88889 2.20833 7.85069 2.625 6.88542C3.04167 5.92014 3.61458 5.07292 4.34375 4.34375C5.07292 3.61458 5.92361 3.04167 6.89583 2.625C7.86806 2.20833 8.90278 2 10 2C11.1111 2 12.1493 2.20833 13.1146 2.625C14.0799 3.04167 14.9271 3.61458 15.6563 4.34375C16.3854 5.07292 16.9583 5.92014 17.375 6.88542C17.7917 7.85069 18 8.88889 18 10C18 11.0972 17.7917 12.1319 17.375 13.1042C16.9583 14.0764 16.3854 14.9271 15.6563 15.6563C14.9271 16.3854 14.0799 16.9583 13.1146 17.375C12.1493 17.7917 11.1111 18 10 18ZM10 16.5C11.8056 16.5 13.3403 15.8681 14.6042 14.6042C15.8681 13.3403 16.5 11.8056 16.5 10C16.5 8.19444 15.8681 6.65972 14.6042 5.39583C13.3403 4.13194 11.8056 3.5 10 3.5C8.19444 3.5 6.65972 4.13194 5.39583 5.39583C4.13194 6.65972 3.5 8.19444 3.5 10C3.5 11.8056 4.13194 13.3403 5.39583 14.6042C6.65972 15.8681 8.19444 16.5 10 16.5Z"
      fill={PrimitivesGreen500}
    />
  </svg>
);

const RedWarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path
      d="M9.99563 14C10.2069 14 10.3854 13.9285 10.5313 13.7856C10.6771 13.6427 10.75 13.4656 10.75 13.2544C10.75 13.0431 10.6785 12.8646 10.5356 12.7188C10.3927 12.5729 10.2156 12.5 10.0044 12.5C9.79312 12.5 9.61458 12.5715 9.46875 12.7144C9.32292 12.8573 9.25 13.0344 9.25 13.2456C9.25 13.4569 9.32146 13.6354 9.46438 13.7813C9.60729 13.9271 9.78438 14 9.99563 14ZM9.25 11H10.75V6H9.25V11ZM10.0058 18C8.90472 18 7.86806 17.7917 6.89583 17.375C5.92361 16.9583 5.07292 16.3854 4.34375 15.6563C3.61458 14.9271 3.04167 14.0767 2.625 13.105C2.20833 12.1333 2 11.0951 2 9.99042C2 8.88569 2.20833 7.85069 2.625 6.88542C3.04167 5.92014 3.61458 5.07292 4.34375 4.34375C5.07292 3.61458 5.92333 3.04167 6.895 2.625C7.86667 2.20833 8.90486 2 10.0096 2C11.1143 2 12.1493 2.20833 13.1146 2.625C14.0799 3.04167 14.9271 3.61458 15.6563 4.34375C16.3854 5.07292 16.9583 5.92167 17.375 6.89C17.7917 7.85847 18 8.89319 18 9.99417C18 11.0953 17.7917 12.1319 17.375 13.1042C16.9583 14.0764 16.3854 14.9271 15.6563 15.6563C14.9271 16.3854 14.0783 16.9583 13.11 17.375C12.1415 17.7917 11.1068 18 10.0058 18ZM10 16.5C11.8056 16.5 13.3403 15.8681 14.6042 14.6042C15.8681 13.3403 16.5 11.8056 16.5 10C16.5 8.19444 15.8681 6.65972 14.6042 5.39583C13.3403 4.13194 11.8056 3.5 10 3.5C8.19444 3.5 6.65972 4.13194 5.39583 5.39583C4.13194 6.65972 3.5 8.19444 3.5 10C3.5 11.8056 4.13194 13.3403 5.39583 14.6042C6.65972 15.8681 8.19444 16.5 10 16.5Z"
      fill={PrimitivesRed500}
    />
  </svg>
);

const ViewContainer = styled(Box)({
  position: 'fixed',
  top: toolbarHeight,
  left: drawerWidth,
  right: 0,
  bottom: 0,
  backgroundColor: PrimitivesWhite,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ScrollContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '24px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const ResultsContent = styled(Box)({
  width: '100%',
  maxWidth: '1024px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const Title = styled(Typography)({
  ...FontTextLgBold,
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: '28px',
  color: PrimitivesBluegray900,
  margin: 0,
});

const RerunButton = styled(Button)({
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBlue900,
  textTransform: 'none',
  border: `1px solid ${PrimitivesBlue900}`,
  borderRadius: '6px',
  padding: '0 24px',
  height: '36px',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: SemanticTokensColorBgButtonNeutralSecondaryHover,
    border: `1px solid ${PrimitivesBlue900}`,
  },
});

const CloseButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '16px',
  alignSelf: 'stretch',
});

const SecondaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 24px',
  height: '36px',
  backgroundColor: 'transparent',
  border: `1px solid ${PrimitivesBlue900}`,
  borderRadius: '6px',
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '1.4em',
  color: PrimitivesBlue900,
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: SemanticTokensColorBgButtonNeutralSecondaryHover,
    border: `1px solid ${PrimitivesBlue900}`,
  },
  '&:disabled': {
    backgroundColor: 'transparent',
    border: `1px solid ${PrimitivesGray300}`,
    color: PrimitivesGray400,
  },
});

const PrimaryButton = styled(Button)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 24px',
  height: '36px',
  backgroundColor: PrimitivesBlue900,
  borderRadius: '6px',
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '1.4em',
  color: PrimitivesWhite,
  textTransform: 'none',
  border: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: PrimitivesBlue700,
  },
  '&:disabled': {
    backgroundColor: PrimitivesGray300,
    color: PrimitivesGray400,
  },
});

const Section = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 24px',
  backgroundColor: PrimitivesBluegray50,
  borderRadius: '8px',
  alignSelf: 'stretch',
});

const SectionTitle = styled(Typography)({
  ...FontTextSmBold,
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
});

const SectionContent = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
});

const FailureSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 24px',
  backgroundColor: PrimitivesRed50,
  border: `1px solid ${PrimitivesRed700}`,
  borderRadius: '8px',
  alignSelf: 'stretch',
});

const FailureTitle = styled(Typography)({
  ...FontTextSmBold,
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
});

const FailureRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const FailureText = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
});

const ReportsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  alignSelf: 'stretch',
});

const ReportsText = styled(Typography)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: PrimitivesBluegray900,
});

const StyledTabs = styled(Tabs)({
  borderBottom: `1px solid ${PrimitivesBluegray100}`,
  minHeight: '40px',
  '& .MuiTabs-indicator': {
    backgroundColor: PrimitivesBlue900,
  },
});

const StyledTab = styled(Tab)({
  ...FontTextSmNormal,
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '1.4em',
  textTransform: 'none',
  color: PrimitivesBluegray600,
  minHeight: '40px',
  padding: '8px 16px',
  '&.Mui-selected': {
    fontWeight: 600,
    color: PrimitivesBlue900,
  },
});

const HtmlContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  '& iframe': {
    width: '100%',
    flex: 1,
    border: 'none',
    minHeight: 0,
  },
});

const TopLevelTabs = styled(Tabs)({
  borderBottom: `1px solid ${PrimitivesBluegray200}`,
  minHeight: '48px',
  '& .MuiTabs-indicator': {
    height: '1.5px',
    backgroundColor: PrimitivesBlue900,
  },
  '& .MuiTabs-flexContainer': {
    gap: '24px',
  },
});

const TopLevelTab = styled(Tab)({
  ...FontTextSmSemibold,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  textTransform: 'none',
  color: PrimitivesBluegray600,
  minHeight: '48px',
  padding: '12px 0',
  minWidth: 'auto',
  '&.Mui-selected': {
    fontWeight: 600,
    color: PrimitivesBlue900,
  },
});

const TabPanel = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  display: active ? 'flex' : 'none',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
}));

const TabBarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '0 32px',
});

const TabBarClose = styled(CloseButton)({
  alignSelf: 'stretch',
  borderRadius: 0,
  borderBottom: `1px solid ${PrimitivesBluegray200}`,
});

function getPayerName(cov, infoboxCoverages) {
  if (cov.payer?.name) return cov.payer.name;
  if (cov.payerName) return cov.payerName;
  const infoCov = infoboxCoverages?.[cov.coordinationTypeId];
  return infoCov?.payer?.name || infoCov?.payer?.entity?.name || '';
}

function passiveOtherPayerLink(reportContent) {
  return (reportContent || '').replace(/<a\b[^>]*\bid="existsLink"[^>]*>/gi, tag => tag.replace(/\s*href="#"/i, ''));
}

function EligibilityAgentView({
  isOpen,
  isLoading,
  changes,
  issues,
  coverages = [],
  coordinationTypes = [],
  infoboxCoverages = {},
  summary,
  changesStatus,
  changesAccepted,
  changesRejected,
  onAccept,
  onReject,
  onClose,
  onRerun,
  isBillerVerified,
  verifiedAt,
  pendingReview = false,
  rerunDisabled = false,
  rerunTooltip = '',
}) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [topTab, setTopTab] = useState(0);
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');
  const tabCoverages = coverages.filter(c => c.reportContent);

  useEffect(() => {
    setSelectedTab(0);
  }, [coverages]);

  useEffect(() => {
    if (!isOpen) {
      setRejectReasonOpen(false);
      setRejectReason('');
      setRejectReasonError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasChanges = changes && changes.length > 0;
  const eligibilityNotes = (issues || []).filter(issue => issue.severity === 'info');
  const failureIssues = (issues || []).filter(issue => issue.severity !== 'info');
  const hasIssues = failureIssues.length > 0;
  const hasSummary = summary && summary.length > 0;
  const autoAccepted = changesStatus === 'accepted';
  const autoAcceptedChanges = changes.filter(c => c.autoAccept);
  const manualReviewChanges = changes.filter(c => !c.autoAccept);
  const hasAutoAcceptedChanges = autoAcceptedChanges.length > 0;
  const hasManualReviewChanges = manualReviewChanges.length > 0;
  const buttonsDisabled = isLoading || changesAccepted || changesRejected;
  const reportsCount = tabCoverages.length;
  const safeTab = Math.min(selectedTab, Math.max(tabCoverages.length - 1, 0));
  const showReportsCount = reportsCount > 0;
  const isBlocking =
    !!pendingReview && !isBillerVerified && hasChanges && !autoAccepted && !changesAccepted && !changesRejected;

  const openRejectDialog = () => {
    setRejectReason('');
    setRejectReasonError('');
    setRejectReasonOpen(true);
  };

  const handleRejectReasonChange = e => {
    setRejectReason(e.target.value);
    if (rejectReasonError) setRejectReasonError('');
  };

  const handleRejectReasonOk = () => {
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setRejectReasonError('Reject reason is required');
      return;
    }
    if (typeof onReject === 'function') onReject(trimmed);
    setRejectReasonOpen(false);
  };

  return (
    <ViewContainer>
      <TabBarRow>
        <TopLevelTabs
          value={topTab}
          onChange={(e, val) => {
            setTopTab(val);
          }}
          sx={{ flex: 1 }}
        >
          <TopLevelTab label="Eligibility Agent Results" />
          <TopLevelTab label="Eligibility Reports" />
          <TopLevelTab label="Audit Log" />
        </TopLevelTabs>
        {!isBlocking && (
          <TabBarClose onClick={onClose} aria-label="Close eligibility view">
            <CloseIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
          </TabBarClose>
        )}
      </TabBarRow>
      <TabPanel active={topTab === 0}>
        <ScrollContainer>
          <ResultsContent>
            <Header>
              <HeaderLeft>
                <MagicWandIcon />
                <Title>Eligibility Agent Results</Title>
              </HeaderLeft>
              <HeaderRight>
                <AmbTooltip title={rerunTooltip}>
                  <span>
                    <RerunButton onClick={onRerun} disabled={isBillerVerified || isLoading || rerunDisabled}>
                      Rerun Agent
                    </RerunButton>
                  </span>
                </AmbTooltip>
              </HeaderRight>
            </Header>
            {isBillerVerified && verifiedAt && (
              <ReportsRow>
                <ReportsText>Insurance Verified on {moment(verifiedAt).format('MM/DD/YYYY [at] HH:mm')}</ReportsText>
              </ReportsRow>
            )}
            {!isBillerVerified && hasChanges && (
              <>
                {hasAutoAcceptedChanges && (
                  <>
                    <ChangesSection
                      title="Auto-Accepted Changes"
                      changes={autoAcceptedChanges}
                      beforeLabel="Before"
                      afterLabel="After"
                    />
                    <ButtonContainer>
                      <PrimaryButton disabled={true}>Changes Accepted</PrimaryButton>
                    </ButtonContainer>
                  </>
                )}
                {hasManualReviewChanges && (
                  <>
                    <ChangesSection
                      title="Manual Review"
                      changes={manualReviewChanges}
                      beforeLabel="Before"
                      afterLabel="After"
                    />
                    <ButtonContainer>
                      {changesAccepted || changesRejected ? (
                        <PrimaryButton disabled={true}>
                          {changesRejected ? 'Changes Rejected' : 'Changes Accepted'}
                        </PrimaryButton>
                      ) : (
                        <>
                          <SecondaryButton onClick={openRejectDialog} disabled={buttonsDisabled}>
                            Reject Changes
                          </SecondaryButton>
                          <PrimaryButton onClick={onAccept} disabled={buttonsDisabled}>
                            {isLoading ? 'Applying...' : 'Accept Changes'}
                          </PrimaryButton>
                        </>
                      )}
                    </ButtonContainer>
                  </>
                )}
              </>
            )}

            <PayerCardsGrid coverages={coverages} infoboxCoverages={infoboxCoverages} />

            {showReportsCount && (
              <Section>
                <ReportsRow>
                  <ReportsText>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Eligibility reports:{' '}
                    </Box>
                    {reportsCount} Attached
                  </ReportsText>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <GreenCheckIcon />
                  </Box>
                </ReportsRow>
              </Section>
            )}

            {hasSummary && (
              <Section>
                <SectionTitle>Summary</SectionTitle>
                <SectionContent>{summary}</SectionContent>
              </Section>
            )}

            {eligibilityNotes.length > 0 && (
              <Section>
                <SectionTitle>Eligibility notes</SectionTitle>
                {eligibilityNotes.map((issue, index) => (
                  <SectionContent key={index}>{issue.message || issue.name}</SectionContent>
                ))}
              </Section>
            )}

            {!isBillerVerified && hasIssues && (
              <FailureSection>
                <FailureTitle>Failure Responses</FailureTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                  {failureIssues.map((issue, index) => (
                    <FailureRow key={index}>
                      <Box sx={{ flexShrink: 0, display: 'flex' }}>
                        <RedWarningIcon />
                      </Box>
                      <FailureText>{issue.message || issue.name || JSON.stringify(issue)}</FailureText>
                    </FailureRow>
                  ))}
                </Box>
              </FailureSection>
            )}
          </ResultsContent>
        </ScrollContainer>
      </TabPanel>

      <TabPanel active={topTab === 1}>
        <ScrollContainer>
          {tabCoverages.length > 0 && (
            <>
              <StyledTabs value={safeTab} onChange={(e, val) => setSelectedTab(val)}>
                {tabCoverages.map((cov, index) => (
                  <StyledTab key={index} label={getPayerName(cov, infoboxCoverages)} />
                ))}
              </StyledTabs>
              <HtmlContainer>
                {tabCoverages[safeTab]?.reportContent && (
                  <iframe
                    title="Eligibility Report"
                    srcDoc={passiveOtherPayerLink(tabCoverages[safeTab].reportContent)}
                    style={{ width: '100%', border: 'none', minHeight: '90vh' }}
                  />
                )}
              </HtmlContainer>
            </>
          )}
        </ScrollContainer>
      </TabPanel>

      <TabPanel active={topTab === 2}>
        <ScrollContainer>{topTab === 2 && <AuditLogContainer />}</ScrollContainer>
      </TabPanel>

      <Dialog
        open={rejectReasonOpen}
        onClose={() => setRejectReasonOpen(false)}
        aria-labelledby="reject-reason-dialog-title"
        PaperProps={{ sx: { width: '480px', maxWidth: '480px', padding: 0, borderRadius: '8px' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
            <Typography
              id="reject-reason-dialog-title"
              sx={{
                ...FontTextXlNormal,
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '1.2em',
                color: PrimitivesBluegray900,
              }}
            >
              Please Input Reject Reason
            </Typography>
            <CloseButton onClick={() => setRejectReasonOpen(false)} aria-label="Close reject reason dialog">
              <CloseIcon style={{ width: 20, height: 20, fill: 'currentColor' }} />
            </CloseButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{ ...FontTextBaseNormal, fontSize: '14px', lineHeight: '1.5em', color: PrimitivesBluegray900 }}
            >
              Rejection reason{' '}
              <Box component="span" sx={{ color: PrimitivesRed500 }}>
                *
              </Box>
            </Typography>
            <TextField
              multiline
              minRows={3}
              value={rejectReason}
              onChange={handleRejectReasonChange}
              placeholder="Enter the reason…"
              inputProps={{ 'aria-label': 'reject reason', 'aria-required': 'true' }}
              error={!!rejectReasonError}
              required
              fullWidth
            />
            {rejectReasonError && (
              <FailureRow>
                <Box sx={{ flexShrink: 0, display: 'flex' }}>
                  <RedWarningIcon />
                </Box>
                <Typography role="alert" sx={{ color: PrimitivesRed800, fontSize: '13px' }}>
                  {rejectReasonError}
                </Typography>
              </FailureRow>
            )}
          </Box>
        </Box>
        <ButtonContainer sx={{ padding: '0 24px 24px' }}>
          <PrimaryButton onClick={handleRejectReasonOk}>OK</PrimaryButton>
        </ButtonContainer>
      </Dialog>
    </ViewContainer>
  );
}

EligibilityAgentView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  changes: PropTypes.arrayOf(PropTypes.shape({})),
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      message: PropTypes.string,
      severity: PropTypes.oneOf(['info', 'error']),
    }),
  ),
  coverages: PropTypes.arrayOf(PropTypes.shape({})),
  coordinationTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      displayName: PropTypes.string,
    }),
  ),
  infoboxCoverages: PropTypes.shape({}),
  summary: PropTypes.string,
  changesStatus: PropTypes.string,
  changesAccepted: PropTypes.bool,
  changesRejected: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRerun: PropTypes.func.isRequired,
  isBillerVerified: PropTypes.bool,
  verifiedAt: PropTypes.string,
  pendingReview: PropTypes.bool,
  rerunDisabled: PropTypes.bool,
  rerunTooltip: PropTypes.string,
};

export default EligibilityAgentView;
